<script>
  import { getContext } from 'svelte';
  import { link } from 'svelte-navigator';
  import { Button, Table } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { getWorkflowActionByValue } from '../../workflow-steps-config';

  /**
   * Array of workflow steps to display in the review.
   * @type {Array}
   * @default []
   */
  export let steps = [];

  /**
   * Object mapping step indices to their execution status ('success', 'error', 'pending').
   * @type {Object}
   * @default {}
   */
  export let stepExecutionStatus = {};

  /**
   * Object mapping step indices to their execution times in milliseconds.
   * @type {Object}
   * @default {}
   */
  export let stepExecutionTimes = {};

  /**
   * Object mapping step indices to their error details (if any).
   * @type {Object}
   * @default {}
   */
  export let stepExecutionErrors = {};

  /**
   * Callback function invoked when the user clicks the rerun button.
   * @type {Function | undefined}
   * @default undefined
   */
  export let onRerun = undefined;

  /**
   * Callback function invoked when the user clicks the return/done button.
   * @type {Function | undefined}
   * @default undefined
   */
  export let onReturn = undefined;

  const { basepath } = getContext('config');

  $: reviewData = steps.map((step, index) => {
    const actionValue = typeof step.action === 'string' ? step.action : step.action.value;
    const stepInfo = getWorkflowActionByValue(actionValue);
    const status = stepExecutionStatus[index] || 'pending';
    const executionTime = stepExecutionTimes[index];

    return {
      index,
      stepNumber: index + 1,
      action: stepInfo || step.action,
      actionValue,
      status,
      executionTime,
      error: stepExecutionErrors[index] || null,
      data: step.data || {},
      category: stepInfo?.categoryLabel || 'Unknown',
      item: stepInfo?.itemLabel || 'Unknown',
      label: stepInfo?.label || actionValue
    };
  });

  const getNavigationUrl = (reviewItem) => {
    const { actionValue, data } = reviewItem;

    const navigationMap = {
      // Assets
      document_create: data.id ? `/manage/assets/documents/${data.id}` : null,
      document_delete: `/manage/assets/documents`,
      document_update: data.id ? `/manage/assets/documents/${data.id}` : null,
      image_single_create: data.id ? `/manage/assets/images/${data.id}` : null,
      image_bulk_create: `/manage/assets/images`,
      image_delete: `/manage/assets/images`,
      image_update: data.id ? `/manage/assets/images/${data.id}` : null,
      video_create: data.id ? `/manage/assets/videos/${data.id}` : null,
      video_delete: `/manage/assets/videos`,
      video_update: data.id ? `/manage/assets/videos/${data.id}` : null,
      // Campaigns
      campaign_clone: data.id ? `/marketing/campaigns/${data.id}` : null,
      campaign_create: data.id ? `/marketing/campaigns/${data.id}` : null,
      campaign_update: data.id ? `/marketing/campaigns/${data.id}` : null,
      campaign_delete: `/marketing/campaigns`,
      // Groups
      group_clone: data.id ? `/manage/groups/${data.id}` : null,
      group_create: data.id ? `/manage/groups/${data.id}` : null,
      group_update: data.id ? `/manage/groups/${data.id}` : null,
      group_delete: `/manage/groups`,
      // Integrations
      integration_create: data.id ? `/manage/integrations/${data.id}` : null,
      integration_update: data.id ? `/manage/integrations/${data.id}` : null,
      integration_delete: `/manage/integrations`,
      // Targeting
      targeting_create: data.id ? `/marketing/targeting/${data.id}` : null,
      targeting_update: data.id ? `/marketing/targeting/${data.id}` : null,
      targeting_delete: data.id ? `/marketing/targeting/${data.id}` : null,
      targeting_group_priority: `/marketing/targeting/groups`,
      // Signatures Studio
      signature_archive: data.id ? `/signatures/studio/${data.id}` : null,
      signature_clone: data.id ? `/signatures/studio/${data.id}` : null,
      signature_clone_domain: data.id ? `/signatures/studio/${data.id}` : null,
      signature_create: data.id ? `/signatures/studio/${data.id}` : null,
      signature_delete: `/signatures/studio`,
      signature_update: data.id ? `/signatures/studio/${data.id}` : null,
      signature_unarchive: data.id ? `/signatures/studio/${data.id}` : null,
      // Signatures Routing
      signature_route_create: `/signatures/routing`,
      signature_route_update: `/signatures/routing`,
      signature_route_delete: `/signatures/routing`,
      signature_route_priority: `/signatures/routing`,
      // Signatures Pusher
      signature_push: `/signatures/pusher`,
      signature_push_repeat: `/signatures/pusher`,
      // Bizcards Studio
      bizcard_clone: data.id ? `/bizcards/studio/edit/${data.id}` : null,
      bizcard_create: data.id ? `/bizcards/studio/edit/${data.id}` : null,
      bizcard_delete: `/bizcards/studio`,
      bizcard_update: data.id ? `/bizcards/studio/edit/${data.id}` : null,
      // Bizcards Routing
      bizcard_route_create: `/bizcards/routing`,
      bizcard_route_update: `/bizcards/routing`,
      bizcard_route_delete: `/bizcards/routing`,
      bizcard_route_priority: `/bizcards/routing`,
      // Domains (Staff)
      domain_billing_update: `/staff/domains`,
      domain_clone: data.id ? `/staff/domains` : null,
      domain_create: data.id ? `/staff/domains?search=query%3A${data.id}` : null,
      domain_mdm: `/staff/domains`,
      // Domain Settings
      domain_settings_update: `/manage/domain-settings`,
      // Users
      user_remove_admin_roles: `/manage/users`,
      user_update: data.id ? `/manage/users/${data.id}` : null
    };

    return navigationMap[actionValue] || null;
  };

  const getStatusDetails = (status) => {
    switch (status) {
      case 'success':
        return {
          icon: 'check-circle',
          color: 'success',
          text: 'Completed'
        };
      case 'error':
        return {
          icon: 'x-circle',
          color: 'danger',
          text: 'Failed'
        };
      case 'pending':
        return {
          icon: 'clock',
          color: 'secondary',
          text: 'Pending'
        };
      default:
        return {
          icon: 'circle',
          color: 'secondary',
          text: 'Unknown'
        };
    }
  };

  const formatExecutionTime = (ms) => {
    if (ms === undefined || ms === null) return null;

    if (ms < 1000) {
      return `${Math.round(ms)}ms`;
    } else if (ms < 60000) {
      return `${(ms / 1000).toFixed(2)}s`;
    } else {
      const minutes = Math.floor(ms / 60000);
      const seconds = ((ms % 60000) / 1000).toFixed(0);
      return `${minutes}m ${seconds}s`;
    }
  };

  $: summary = {
    total: reviewData.length,
    completed: reviewData.filter((r) => r.status === 'success').length,
    failed: reviewData.filter((r) => r.status === 'error').length,
    totalTime: reviewData.reduce((sum, r) => sum + (r.executionTime || 0), 0)
  };

  const getResourceName = (reviewItem) => {
    const { data, actionValue } = reviewItem;

    if (actionValue === 'targeting_create') {
      return data.id ? `${data.id} targeting rule created` : 'Targeting rule created';
    }

    if (actionValue === 'targeting_delete') {
      return data.id ? `${data.id} targeting rule deleted` : 'Targeting rule deleted';
    }

    if (actionValue === 'targeting_update') {
      return data.id ? `${data.id} targeting rule updated` : 'Targeting rule updated';
    }

    if (actionValue === 'targeting_group_priority') {
      const changedCount =
        data.items?.filter((item, index) => {
          const originalItem = data.rollbackData?.originalTargeting?.departments?.[index];
          return (
            originalItem &&
            JSON.stringify(item) !==
              JSON.stringify({ ...originalItem, priority: index + 1, label: item.label, value: item.value })
          );
        }).length || 0;

      return changedCount > 0
        ? `${changedCount} targeting group${changedCount !== 1 ? 's' : ''} reordered`
        : 'Targeting group priorities updated';
    }

    // Handle user actions (enable, disable, reminder)
    if (actionValue === 'user_enable' || actionValue === 'user_disable' || actionValue === 'user_reminder') {
      const count = data.ids?.length || data.rollbackData?.successCount || 0;
      const actionName =
        actionValue === 'user_enable' ? 'enabled' : actionValue === 'user_disable' ? 'disabled' : 'sent reminders to';
      return count > 0 ? `${count} user${count !== 1 ? 's' : ''} ${actionName}` : 'No users processed';
    }

    // Handle remove admin roles
    if (actionValue === 'user_remove_admin_roles') {
      const count = data.ids?.length || data.rollbackData?.successCount || 0;
      return count > 0 ? `Admin roles removed from ${count} user${count !== 1 ? 's' : ''}` : 'No users processed';
    }

    // Handle user sync
    if (actionValue === 'user_create') {
      const results = data.results;
      if (results) {
        const totalProcessed =
          (results.created?.length || 0) +
          (results.updated?.length || 0) +
          (results.disabled?.length || 0) +
          (results.restored?.length || 0);
        return `${totalProcessed} user${totalProcessed !== 1 ? 's' : ''} synced`;
      }
      return 'User sync completed';
    }

    // Handle bulk asset creation
    if (actionValue === 'image_bulk_create') {
      const count = data.ids?.length || 0;
      return count > 0 ? `${count} image${count !== 1 ? 's' : ''} uploaded` : 'Images uploaded';
    }

    // Handle single asset creation
    if (actionValue === 'image_single_create') {
      return data.id || 'Image uploaded';
    }

    if (actionValue === 'document_create') {
      return data.id || 'Document uploaded';
    }

    if (actionValue === 'video_create') {
      return data.id || 'Video uploaded';
    }

    // Handle asset deletion
    if (actionValue === 'image_delete') {
      return data.id || 'Image deleted';
    }

    if (actionValue === 'document_delete') {
      return data.id || 'Document deleted';
    }

    if (actionValue === 'video_delete') {
      return data.id || 'Video deleted';
    }

    // Handle signature studio actions
    if (actionValue === 'signature_clone') {
      return data.id || 'Signature cloned';
    }

    if (actionValue === 'signature_clone_domain') {
      return data.id || 'Signature cloned to domain';
    }

    if (actionValue === 'signature_create') {
      return data.id || 'Signature created';
    }

    if (actionValue === 'signature_archive') {
      return data.id || 'Signature archived';
    }

    if (actionValue === 'signature_unarchive') {
      return data.id || 'Signature unarchived';
    }

    if (actionValue === 'signature_delete') {
      return data.id || 'Signature deleted';
    }

    if (actionValue === 'signature_update') {
      return data.id || 'Signature updated';
    }

    // Handle signature routing
    if (actionValue === 'signature_route_create') {
      return data.id && data.priority ? `Route created for ${data.id} (#${data.priority})` : 'Signature route created';
    }

    if (actionValue === 'signature_route_update') {
      return data.id && data.priority ? `Route updated for ${data.id} (#${data.priority})` : 'Signature route updated';
    }

    if (actionValue === 'signature_route_delete') {
      return data.id && data.priority ? `Route deleted for ${data.id} (#${data.priority})` : 'Signature route deleted';
    }

    if (actionValue === 'signature_route_priority') {
      const changedCount =
        data.items?.filter((item, index) => {
          const originalItem = data.rollbackData?.originalItems?.[index];
          return originalItem && item.priority !== originalItem.priority;
        }).length || 0;

      return changedCount > 0
        ? `${changedCount} signature route${changedCount !== 1 ? 's' : ''} reordered`
        : 'Signature route priorities updated';
    }

    // Handle bizcard studio actions
    if (actionValue === 'bizcard_clone') {
      return data.id || 'Business card cloned';
    }

    if (actionValue === 'bizcard_create') {
      return data.id || 'Business card created';
    }

    if (actionValue === 'bizcard_delete') {
      return data.id || 'Business card deleted';
    }

    if (actionValue === 'bizcard_update') {
      return data.id || 'Business card updated';
    }

    // Handle bizcard routing
    if (actionValue === 'bizcard_route_create') {
      return data.id ? `Route created for ${data.id}` : 'Business card route created';
    }

    if (actionValue === 'bizcard_route_update') {
      return data.id && data.priority
        ? `Route updated for ${data.id} (#${data.priority})`
        : 'Business card route updated';
    }

    if (actionValue === 'bizcard_route_delete') {
      return data.id ? `Route deleted for ${data.id}` : 'Business card route deleted';
    }

    if (actionValue === 'bizcard_route_priority') {
      const changedCount =
        data.items?.filter((item, index) => {
          const originalItem = data.rollbackData?.originalItems?.[index];
          return originalItem && item.priority !== originalItem.priority;
        }).length || 0;

      return changedCount > 0
        ? `${changedCount} business card route${changedCount !== 1 ? 's' : ''} reordered`
        : 'Business card route priorities updated';
    }

    // Handle signature pusher
    if (actionValue === 'signature_push') {
      return data.id && data.type ? `Pushed ${data.type} to ${data.id}` : 'New push';
    }

    if (actionValue === 'signature_push_repeat') {
      return data.id && data.type ? `Repeated ${data.type} push to ${data.id}` : 'Repeat push';
    }

    // Handle campaign operations
    if (actionValue === 'campaign_delete') {
      return data.id || data.rulename || 'Campaign deleted';
    }

    if (actionValue === 'campaign_update') {
      return data.id || 'Campaign updated';
    }

    // Handle group operations
    if (actionValue === 'group_delete') {
      return data.id || data.groupname || 'Group deleted';
    }

    if (actionValue === 'group_update') {
      return data.id || data.groupname || 'Group updated';
    }

    // Handle integration operations
    if (actionValue === 'integration_create') {
      return data.id || data.name || 'Integration created';
    }

    if (actionValue === 'integration_update') {
      return data.id || 'Integration updated';
    }

    if (actionValue === 'integration_delete') {
      return data.id || data.name || 'Integration deleted';
    }

    // Handle user update
    if (actionValue === 'user_update') {
      return data.id || 'User updated';
    }

    // Handle domain billing update
    if (actionValue === 'domain_billing_update') {
      return data.id ? `${data.id} billing IDs updated` : 'Billing IDs updated';
    }

    // Handle domain settings update
    if (actionValue === 'domain_settings_update') {
      const settingsCount =
        data.rollbackData?.updatedSettingsList?.length || Object.keys(data.rollbackData?.updates || {}).length;
      return settingsCount > 0
        ? `${settingsCount} setting${settingsCount !== 1 ? 's' : ''} updated`
        : 'Domain settings updated';
    }

    return data.name || data.rulename || data.title || data.groupname || data.id || 'N/A';
  };

  const getUpdateDetails = (reviewItem) => {
    const { data, actionValue } = reviewItem;

    if (!actionValue.endsWith('_update')) {
      return null;
    }

    // Handle domain_settings_update specially - use updatedSettingsList
    if (actionValue === 'domain_settings_update') {
      const settingsList = data.rollbackData?.updatedSettingsList;
      if (!settingsList || settingsList.length === 0) {
        return null;
      }

      return {
        updates: settingsList.map((setting) => ({
          field: [setting.category, setting.subsection, setting.label].filter(Boolean).join(' > ')
        }))
      };
    }

    // Handle other update actions
    const updates = data.updates;
    if (!updates) {
      return null;
    }

    const updateEntries = Object.entries(updates);

    if (updateEntries.length === 0) {
      return null;
    }

    return {
      count: updateEntries.length,
      updates: updateEntries.map(([key, value]) => ({
        field: value?.name || key,
        value: formatUpdateValue(value?.value !== undefined ? value.value : value)
      }))
    };
  };

  const formatUpdateValue = (value) => {
    if (value === null || value === undefined) {
      return 'null';
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    if (Array.isArray(value)) {
      return value.length === 0 ? '[]' : `[${value.length} items]`;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value).length > 50 ? `{${Object.keys(value).length} fields}` : JSON.stringify(value);
    }
    if (typeof value === 'string') {
      return value.length > 50 ? `${value.substring(0, 50)}...` : value;
    }
    return String(value);
  };

  const getAdditionalDetails = (reviewItem) => {
    const { data, actionValue } = reviewItem;

    // Show domain list for clone to domain
    if (actionValue === 'signature_clone_domain' && data.domains?.length > 0) {
      return {
        type: 'domains',
        label: 'Cloned to domains',
        items: data.domains
      };
    }

    return null;
  };
</script>

<div class="review-step">
  <div class="review-summary">
    <div class="summary-card">
      <div class="summary-icon text-primary">
        <Icon name="list-check" size="24" />
      </div>
      <div class="summary-content">
        <div class="summary-label">Total Steps</div>
        <div class="summary-value">{summary.total}</div>
      </div>
    </div>

    <div class="summary-card">
      <div class="summary-icon text-success">
        <Icon name="check-circle" size="24" />
      </div>
      <div class="summary-content">
        <div class="summary-label">Completed</div>
        <div class="summary-value">{summary.completed}</div>
      </div>
    </div>

    <div class="summary-card">
      <div class="summary-icon text-danger">
        <Icon name="x-circle" size="24" />
      </div>
      <div class="summary-content">
        <div class="summary-label">Failed</div>
        <div class="summary-value">{summary.failed}</div>
      </div>
    </div>

    <div class="summary-card">
      <div class="summary-icon text-primary">
        <Icon name="clock" size="24" />
      </div>
      <div class="summary-content">
        <div class="summary-label">Total Time</div>
        <div class="summary-value">{formatExecutionTime(summary.totalTime) || '—'}</div>
      </div>
    </div>
  </div>

  <div class="review-table-container">
    <Table striped hover responsive>
      <thead>
        <tr>
          <th style="width: 60px;">#</th>
          <th style="width: 120px;">Status</th>
          <th>Category</th>
          <th>Section</th>
          <th>Action</th>
          <th>Resource</th>
          <th style="width: 100px;">Time</th>
          <th style="width: 120px;">Actions</th>
        </tr>
      </thead>
      <tbody>
        {#each reviewData as item}
          {@const statusDetails = getStatusDetails(item.status)}
          {@const navigationUrl = getNavigationUrl(item)}
          {@const resourceName = getResourceName(item)}
          {@const updateDetails = getUpdateDetails(item)}
          {@const additionalDetails = getAdditionalDetails(item)}
          <tr>
            <td class="text-center">
              <strong>{item.stepNumber}</strong>
            </td>
            <td>
              <div class="status-badge">
                <Icon name={statusDetails.icon} class="text-{statusDetails.color}" />
                <span class="text-{statusDetails.color}">{statusDetails.text}</span>
              </div>
            </td>
            <td>{item.category}</td>
            <td>{item.item}</td>
            <td><strong>{item.label}</strong></td>
            <td>
              {#if item.status === 'success'}
                <div class="resource-info">
                  <span class="resource-name">{resourceName}</span>
                  {#if updateDetails}
                    <div class="update-details">
                      {#if updateDetails.count}
                        <span class="update-count"
                          >{updateDetails.count} field{updateDetails.count > 1 ? 's' : ''} updated:</span
                        >
                      {/if}
                      <div class="update-list">
                        {#each updateDetails.updates as update}
                          <div class="update-item">
                            <span class="update-field">{update.field}{update.value ? ':' : ''}</span>
                            {#if update.value}
                              <span class="update-value">{update.value}</span>
                            {/if}
                          </div>
                        {/each}
                      </div>
                    </div>
                  {/if}
                  {#if additionalDetails}
                    <div class="additional-details">
                      <span class="details-label">{additionalDetails.label}:</span>
                      <div class="details-list">
                        {#each additionalDetails.items as detailItem}
                          <span class="detail-item">{detailItem}</span>
                        {/each}
                      </div>
                    </div>
                  {/if}
                </div>
              {:else if item.status === 'error'}
                <span class="text-muted">—</span>
              {:else}
                <span class="text-muted">Not executed</span>
              {/if}
            </td>
            <td>
              {#if item.executionTime !== undefined && item.executionTime !== null}
                <span class="execution-time">{formatExecutionTime(item.executionTime)}</span>
              {:else}
                <span class="text-muted">—</span>
              {/if}
            </td>
            <td>
              {#if navigationUrl && item.status === 'success'}
                <a href="{basepath}{navigationUrl}" target="_blank" use:link>
                  <Button size="sm" color="primary" outline>View</Button>
                </a>
              {:else}
                <Button size="sm" color="secondary" outline disabled>View</Button>
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </Table>
  </div>

  <div class="review-actions">
    {#if onReturn}
      <div class="return-btn">
        <Button color="secondary" on:click={onReturn}>Done</Button>
      </div>
    {/if}
    {#if onRerun}
      <div class="return-btn">
        <Button color="primary" on:click={onRerun}>Rerun</Button>
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  .review-step {
    padding: var(--padding-large) 0;
  }

  .review-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 32px;
  }

  .summary-card {
    align-items: center;
    background: white;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    display: flex;
    gap: 16px;
    padding: 20px;
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }
  }

  .summary-icon {
    align-items: center;
    display: flex;
    justify-content: center;
  }

  .summary-content {
    flex: 1;
  }

  .summary-label {
    color: var(--color-gray-600);
    font-size: 14px;
    margin-bottom: 4px;
  }

  .summary-value {
    color: var(--color-gray-900);
    font-size: 28px;
    font-weight: 600;
    line-height: 1;
  }

  .review-table-container {
    background: white;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    margin-bottom: 24px;
    overflow: hidden;

    :global(table) {
      margin-bottom: 0;
    }

    :global(th) {
      background: var(--color-gray-100);
      border-bottom: 2px solid var(--border-color);
      color: var(--color-gray-700);
      font-size: 13px;
      font-weight: 600;
      padding: 12px 16px;
      text-transform: uppercase;
    }

    :global(td) {
      padding: 12px 16px;
      vertical-align: middle;
    }
  }

  .status-badge {
    align-items: center;
    display: flex;
    gap: 6px;
    font-weight: 500;
  }

  .resource-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .resource-name {
    color: var(--color-gray-800);
    font-family: var(--font-family-mono, monospace);
    font-size: 13px;
  }

  .update-details {
    display: flex;
    flex-direction: column;
    font-size: 12px;
    gap: 6px;
    margin-top: 4px;
  }

  .update-count {
    color: var(--color-gray-600);
    font-weight: 500;
  }

  .update-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 2px;
  }

  .update-item {
    display: flex;
    gap: 6px;
    align-items: baseline;
  }

  .update-field {
    color: var(--color-gray-600);
    font-family: var(--font-family-mono, monospace);
    font-size: 11px;
    font-weight: 500;
  }

  .update-value {
    color: var(--color-gray-800);
    font-family: var(--font-family-mono, monospace);
    font-size: 11px;
    word-break: break-word;
  }

  .additional-details {
    display: flex;
    flex-direction: column;
    font-size: 12px;
    gap: 6px;
    margin-top: 8px;
  }

  .details-label {
    color: var(--color-gray-600);
    font-weight: 500;
  }

  .details-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 2px;
  }

  .detail-item {
    background: var(--color-gray-100);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    color: var(--color-gray-700);
    font-family: var(--font-family-mono, monospace);
    font-size: 11px;
    padding: 2px 6px;
  }

  .execution-time {
    color: var(--color-gray-700);
    font-family: var(--font-family-mono, monospace);
    font-size: 12px;
    font-weight: 500;
  }

  .review-actions {
    bottom: 16px;
    display: flex;
    gap: 8px;
    position: absolute;
    right: 16px;
  }
</style>
